import React, { useState } from 'react'
import Dropzone from 'react-dropzone'

import { Box, Button, Text } from '@factorialco/deprecated-design-system'
import { F0Dialog } from '@factorialco/f0-react'

import TextArea from 'modules/core/components/TextArea'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { useComplaintMessages } from 'modules/complaints/hooks/useComplaintMessages'

const FileList = ({ files }: { files: File[] }) => {
  if (files.length === 0) return null

  return (
    <Box gap="s4">
      {files.map((file) => (
        <Box key={file.name}>
          <Text.Normal size="200" weight="semibold" ellipsis>
            {file.name}
          </Text.Normal>
        </Box>
      ))}
    </Box>
  )
}

const FilesButton = ({
  files,
  setFiles,
  disabled,
}: {
  files: File[]
  setFiles: (files: File[]) => void
  disabled: boolean
}) => {
  if (files.length === 0) {
    return (
      <Dropzone onDrop={setFiles} disabled={disabled}>
        {({ getRootProps, getInputProps }) => (
          <div {...getRootProps()}>
            <input {...getInputProps()} />
            <Button type="secondary" disabled={disabled} onClick={() => null} iconName="add">
              {i18n.t('complaints.activity.add_documents')}
            </Button>
          </div>
        )}
      </Dropzone>
    )
  }

  const removeFiles = () => setFiles([])

  return (
    <Button onClick={removeFiles} type="secondary" disabled={disabled} iconName="remove">
      {i18n.t('complaints.activity.remove_documents')}
    </Button>
  )
}

interface NewMessageProps {
  complaintId: number
  blocked?: boolean
}

const NewMessage: React.FC<NewMessageProps> = ({ complaintId, blocked = false }) => {
  const [text, setText] = useState('')
  const [files, setFiles] = useState<File[]>([])
  const [showConfirmModal, setShowConfirmModal] = useState(false)

  const { createMessage, isCreating } = useComplaintMessages({
    complaintId,
    enabled: false,
  })

  const handleSubmit = () => {
    if (text.length === 0 || isCreating) return
    setShowConfirmModal(true)
  }

  const confirmSubmit = async () => {
    if (text.length === 0 || isCreating) return

    try {
      await createMessage({
        complaintId,
        text,
        files,
      })

      setText('')
      setFiles([])
      setShowConfirmModal(false)
    } catch (error) {
      app.setFlash({
        type: 'wrong',
        message: i18n.t('common.error'),
      })
    }
  }

  const handleCloseModal = () => {
    setShowConfirmModal(false)
  }

  const disabled = isCreating || blocked
  const buttonDisabled = text.length === 0 || isCreating

  return (
    <>
      <Box
        background="grey100"
        padding="s24"
        border={{
          all: {
            color: 'grey400',
            width: 's1',
            style: 'solid',
          },
        }}
        borderRadius={{
          all: 'abs004',
        }}
      >
        <Box gap="s24">
          <TextArea
            value={text}
            onChange={setText}
            placeholder={i18n.t('complaints.activity.placeholder')}
            readonly={disabled}
          />
          <FileList files={files} />
          <Box flexDirection="row" gap="s8" justifyContent="flexEnd">
            <FilesButton files={files} setFiles={setFiles} disabled={disabled} />
            <Button onClick={handleSubmit} disabled={buttonDisabled} type="negative">
              {isCreating ? i18n.t('common.saving') : i18n.t('complaints.activity.post')}
            </Button>
          </Box>
        </Box>
      </Box>
      <F0Dialog
        isOpen={showConfirmModal}
        onClose={handleCloseModal}
        title={i18n.t('complaints.activity.submit_response_title')}
        primaryAction={{
          label: isCreating ? i18n.t('common.saving') : i18n.t('common.save'),
          onClick: confirmSubmit,
          disabled: isCreating,
          loading: isCreating,
        }}
        secondaryAction={{
          label: i18n.t('common.cancel'),
          onClick: handleCloseModal,
          disabled: isCreating,
        }}
      >
        <Box gap="s12">
          <Text.Normal>{i18n.t('complaints.activity.submit_response_description')}</Text.Normal>
        </Box>
      </F0Dialog>
    </>
  )
}

export default NewMessage
